import { useHistory, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import { RevenueHeroEvent, SignUpEvent } from "../../constants/signup.events";
import { createAnalyticsPayload } from "../../utils";
import { StepRenderer } from "../StepRenderer/StepRenderer";
import { useExperiment } from "../../../../hooks/useExperiment";

interface SignupFlowContainerProps {
  machineState: any;
  machineSend: any;
}

const SignupFlowContainer: React.FC<SignupFlowContainerProps> = ({
  machineState: state,
  machineSend: send,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const { useExperimentIsOn } = useExperiment();
  const isExperimentVariantOn = useExperimentIsOn("signup-flow-experiment");
  const experimentVariant = isExperimentVariantOn ? "variant" : "control";

  const currentStepName = String(state.value);
  const previousStepRef = useRef<string | null>(null);
  const isInitialRenderRef = useRef(true);

  useEffect(() => {
    // Skip tracking on initial render
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      previousStepRef.current = currentStepName;
      return;
    }

    // Only track analytics if we've transitioned from a previous state
    if (
      previousStepRef.current !== currentStepName &&
      previousStepRef.current !== null
    ) {
      // Track analytics for the *previous* state we're transitioning from
      const previousState = state.machine.states[previousStepRef.current];
      const analyticsEvent = previousState?.meta?.analytics?.event;

      if (analyticsEvent) {
        const analyticsPayload = createAnalyticsPayload(
          state.context,
          previousStepRef.current,
        );

        const enhancedPayload = {
          ...analyticsPayload,
          stateMachineId: state.machine.id,
          experimentVariant: experimentVariant,
          stepNumber:
            state.machine.states[previousStepRef.current]?.meta?.step_number,
        };

        trackEvent(analyticsEvent, enhancedPayload);
      }
    }
    // Update the ref with the current state
    previousStepRef.current = currentStepName;
  }, [
    currentStepName,
    state.context.formData,
    state.machine.states,
    trackEvent,
    experimentVariant,
    state.machine.id,
  ]);

  useEffect(() => {
    const handleMessage = (ev: MessageEvent) => {
      const event = ev.data.type as RevenueHeroEvent;

      switch (event) {
        case "MEETING_BOOKED":
          trackEvent(SignUpEvent.DemoBooked, {
            experimentVariant: experimentVariant,
            machineId: state.machine.id,
          });
          break;
        case "PROSPECT_DISQUALIFIED":
          trackEvent(SignUpEvent.SignUpDisqualified, {
            experimentVariant: experimentVariant,
            machineId: state.machine.id,
          });
          send({ type: "DISQUALIFIED" });
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [trackEvent, send, state, experimentVariant]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlStep = searchParams.get("step");

    if (!urlStep || urlStep !== state.value) {
      const correctParams = new URLSearchParams(location.search);
      correctParams.set("step", state.value as string);
      history.replace({
        search: correctParams.toString(),
      });
    }
  }, [state.value, location.search, history]);

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-space-y-8">
      <StepRenderer
        variant={experimentVariant}
        currentState={currentStepName}
      />
    </div>
  );
};

export default SignupFlowContainer;
